import { TankBalance, TankSurfaceLevel } from '@api/queries/tanks';
import React, { Component } from 'react';
import styled from 'styled-components';

//#region styles

const Container = styled.div`
  display: flex;
  align-items: center;
`;

const ProgressContainer = styled.div`
  position: relative;
  height: 20px;
  flex: 1;
`;

interface BalanceContainerProps {
  $textWidth?: number;
}

const BalanceContainer = styled.div<BalanceContainerProps>`
  font-size: 14px;
  color: #333;
  margin-left: 5px;
  width: ${props => (props.$textWidth ? props.$textWidth + 'px' : 'auto')};
`;

const Empty = styled.div`
  position: absolute;
  height: 100%;
  width: 100%;
  background: #efefef;
  border: 1px solid #333;
  border-radius: 5px;
`;

const Filler = styled.div<FillerProps>`
  position: absolute;
  right: ${props => (props.$isNegative ? '0' : 'auto')};
  height: 100%;
  background: ${props => props.$color};
  border: 1px solid #333;
  border-radius: 5px;
  width: ${props => props.$percentage}%;
  visibility: ${props => (props.$percentage ? 'visible' : 'hidden')};
`;

const Percentage = styled.div`
  text-align: center;
  position: absolute;
  width: 100%;
  height: 100%;
  line-height: 20px;
  font-size: 10px;
  color: #000;
`;

const Separator = styled.span`
  margin: 0 2px;
`;

//#endregion

interface FillerProps {
  $percentage: number;
  $color: string;
  $isNegative: boolean;
}

interface TankBalanceProgressProps {
  tankBalance: TankBalance | TankSurfaceLevel;
  showUnit?: boolean;
  showKilograms?: boolean;
  textWidth?: number;
}

export const TankBalanceProgress: React.FunctionComponent<TankBalanceProgressProps> = props => {
  const { tankBalance, showKilograms, showUnit, textWidth } = props;

  if (tankBalance === null || tankBalance === undefined) {
    return null;
  }

  const weight = showKilograms ? tankBalance.weight : inTons(tankBalance.weight);
  const maxWeight = showKilograms ? tankBalance.weightCapasity : inTons(tankBalance.weightCapasity);
  const unit = showUnit ? (showKilograms ? 'kg' : 't') : null;

  return (
    <TankProgress
      value={weight}
      maxValue={maxWeight}
      unit={unit}
      fillPercentage={tankBalance.fillPercentage}
      textWidth={textWidth}
    />
  );
};

interface TankProgressProps {
  value: number;
  maxValue: number;
  fillPercentage: number;
  unit?: string;
  textWidth?: number;
}

const TankProgress = (props: TankProgressProps) => {
  const { value, maxValue, unit, fillPercentage } = props;
  const percentage = Math.min(Math.abs(fillPercentage), 100);
  const color = getColor(percentage, value, maxValue);
  const balanceText =
    maxValue !== 0 ? (
      <span className="balance-text">
        {value.toLocaleString('fi')}
        <Separator>/</Separator>
        {maxValue.toLocaleString('fi')}
        {unit && ' ' + unit}
      </span>
    ) : (
      '--'
    );

  return (
    <Container>
      <ProgressContainer>
        <Empty />
        <Filler $percentage={percentage} $color={color} $isNegative={value < 0} />
        <Percentage>{percentage} %</Percentage>
      </ProgressContainer>
      <BalanceContainer $textWidth={props.textWidth}>{balanceText}</BalanceContainer>
    </Container>
  );
};

const inTons = (kilos: number) => Math.ceil(kilos / 1000);

const getColor = (percentage: number, value: number, maxValue: number) => {
  if (value < 0 || value > maxValue) {
    return 'red';
  }

  const colors = ['#01E279'];

  // return 'linear-gradient(to right, #01E279, green ,orange, #FE5501, red);';

  if (percentage > 25) {
    colors.push('green');
  }
  if (percentage > 50) {
    colors.push('orange');
  }
  if (percentage > 75) {
    colors.push('#FE5501');
  }
  if (percentage > 90) {
    colors.push('red');
  }

  return colors.length > 1 ? `linear-gradient(to right, ${colors.join(', ')});` : colors[0];
};

export default TankBalanceProgress;
